// Resolves the active organization (organization-access spec): single membership by default, a
// simple chooser when there are several, and the terminal message for worker-only users.
import { platformGetMeOptions, type MeOut } from "@manapp/api-client"
import { Button, Select, errorMessage, isLanguage, useTranslation } from "@manapp/ui"
import { useQuery } from "@tanstack/react-query"
import { useEffect, useState, type ReactNode } from "react"

import { isProblem, setActiveOrganization, setApiLanguage } from "../api"
import { useAuthController } from "../auth/auth-context"
import { OrgContext } from "./org-context"
import { CenteredScreen, Message, PrimaryAction } from "./screens"

const SELECTED_KEY = "manapp.organization"

export function OrgGate({ children }: { children: ReactNode }) {
  const { t, i18n } = useTranslation()
  const controller = useAuthController()
  const [selected, setSelected] = useState<string | null>(() => sessionStorage.getItem(SELECTED_KEY))
  // The chosen organization is sent with /me itself, so the capabilities it returns are that
  // organization's; the query is keyed by the choice and keeps the previous data while switching.
  if (selected) setActiveOrganization(selected)
  const meOptions = platformGetMeOptions()
  const me = useQuery({
    ...meOptions,
    queryKey: [...meOptions.queryKey, selected ?? "auto"] as unknown as typeof meOptions.queryKey,
    retry: false,
    placeholderData: (previous) => previous,
  })

  // With several memberships but one office organization, /me cannot know which organization is
  // meant until it is chosen: pin it so the next /me resolves that organization's capabilities.
  const offices = (me.data?.memberships ?? []).filter((m) => m.active && m.role !== "worker")
  const resolved = offices.length === 1 ? offices[0]?.organization_id : offices.find((m) => m.organization_id === selected)?.organization_id
  useEffect(() => {
    if (me.data && resolved && me.data.active_organization_id !== resolved && selected !== resolved) {
      sessionStorage.setItem(SELECTED_KEY, resolved)
      setSelected(resolved)
    }
  }, [me.data, resolved, selected])

  useEffect(() => {
    const language = me.data?.language
    if (isLanguage(language) && i18n.language !== language) {
      void i18n.changeLanguage(language)
      setApiLanguage(language)
    }
  }, [me.data?.language, i18n])

  if (me.isPending) return <CenteredScreen title={t("app.loading")} />
  // Only a failed first load blocks: a failed background refetch keeps the loaded app (and any
  // unsaved work in it) instead of replacing everything with an access screen.
  if (me.isError && !me.data) {
    const code = isProblem(me.error) ? me.error.code : "internal.error"
    const text =
      code === "auth.not_invited"
        ? t("access.notInvited")
        : code === "auth.invitation_unavailable"
          ? t("access.unavailable")
          : errorMessage(t, code)
    return (
      <CenteredScreen title={t("access.notInvitedTitle")}>
        <Message>{text}</Message>
        <PrimaryAction onClick={() => void controller.signOut()}>{t("auth.signOut")}</PrimaryAction>
      </CenteredScreen>
    )
  }

  const data: MeOut | undefined = me.data
  if (!data) return <CenteredScreen title={t("app.loading")} />
  const active = data.memberships.filter((m) => m.active)
  if (active.length === 0) {
    return (
      <CenteredScreen title={t("access.notInvitedTitle")}>
        <Message>{t("access.noMembership")}</Message>
        <PrimaryAction onClick={() => void controller.signOut()}>{t("auth.signOut")}</PrimaryAction>
      </CenteredScreen>
    )
  }
  if (active.every((m) => m.role === "worker")) {
    return (
      <CenteredScreen title={t("worker.title")}>
        <Message>{t("worker.body")}</Message>
        <PrimaryAction onClick={() => void controller.signOut()}>{t("auth.signOut")}</PrimaryAction>
      </CenteredScreen>
    )
  }
  const office = active.filter((m) => m.role !== "worker")
  const current = office.length === 1 ? office[0] : office.find((m) => m.organization_id === selected)
  if (!current) {
    return <OrgChooser options={office.map((m) => ({ id: m.organization_id, name: m.organization_name }))} onChoose={(id) => {
      sessionStorage.setItem(SELECTED_KEY, id)
      setSelected(id)
    }} />
  }
  setActiveOrganization(current.organization_id)
  return (
    <OrgContext.Provider
      value={{
        me: data,
        organizationId: current.organization_id,
        role: current.role,
        // /me reports capabilities for its active organization only; anything else shows none.
        capabilities: data.active_organization_id === current.organization_id ? (data.capabilities ?? []) : [],
        timeZone: data.time_zone ?? "Europe/Tallinn",
      }}
    >
      {children}
    </OrgContext.Provider>
  )
}

function OrgChooser({ options, onChoose }: { options: { id: string; name: string }[]; onChoose: (id: string) => void }) {
  const { t } = useTranslation()
  const [value, setValue] = useState(options[0]?.id ?? "")
  return (
    <CenteredScreen title={t("org.chooseTitle")}>
      <Select value={value} onValueChange={setValue}>
        <Select.Trigger aria-label={t("org.chooseTitle")}>
          <Select.Value />
        </Select.Trigger>
        <Select.Content>
          {options.map((o) => (
            <Select.Item key={o.id} value={o.id}>
              {o.name}
            </Select.Item>
          ))}
        </Select.Content>
      </Select>
      <Button onClick={() => onChoose(value)}>{t("org.choose")}</Button>
    </CenteredScreen>
  )
}
